import gql from "graphql-tag";
import { API, graphqlOperation } from "aws-amplify";
import { NolaApiInitializer } from "@/initializer";
import { UserEmailSettings } from "../models/userEmail";
import { UserClient } from "./userClient";

interface IUserEmailClient {
  fetchEmailReceiveSettings: () => Promise<UserEmailSettings>;
  updateEmailReceiveSettings: (receiveAggregation: string) => Promise<UserEmailSettings>;
}

const DEFAULT_SETTINGS: UserEmailSettings = {
  receiveAggregation: null,
  updatedAt: null,
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getGraphqlErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "";
  }

  if ("message" in error && typeof (error as { message?: unknown }).message === "string") {
    return (error as { message: string }).message;
  }

  if ("errors" in error && Array.isArray((error as { errors?: unknown[] }).errors)) {
    const firstError = (error as { errors: Array<{ message?: string }> }).errors[0];
    return firstError?.message || "";
  }

  return "";
}

function isUserEmailNotFoundError(error: unknown): boolean {
  return getGraphqlErrorMessage(error).includes("user_email not found");
}

async function requestGraphql<T>(query: string | ReturnType<typeof gql>, variables?: Record<string, unknown>): Promise<T> {
  NolaApiInitializer.init();
  const result = (await API.graphql(graphqlOperation(query, variables))) as {
    data?: T;
    errors?: Array<{ message?: string }>;
  };

  if (result.errors?.length) {
    throw result.errors[0];
  }

  if (!result.data) {
    throw new Error("GraphQL response is empty");
  }

  return result.data;
}

async function waitForSyncedUserEmail(
  fetchSettings: () => Promise<UserEmailSettings | null>,
  remainingAttempts: number,
  delayMs: number
): Promise<UserEmailSettings | null> {
  if (remainingAttempts <= 0) {
    return null;
  }

  await sleep(delayMs);
  const settings = await fetchSettings();
  if (settings) {
    return settings;
  }

  return waitForSyncedUserEmail(fetchSettings, remainingAttempts - 1, delayMs);
}

async function fetchEmailReceiveSettingsRaw(): Promise<UserEmailSettings | null> {
  try {
    const data = await requestGraphql<{ emailReceiveSettings: UserEmailSettings }>(fetchEmailReceiveSettingsQuery);
    return data.emailReceiveSettings;
  } catch (error) {
    if (isUserEmailNotFoundError(error)) {
      return null;
    }
    throw error;
  }
}

export class UserEmailClient implements IUserEmailClient {
  async fetchEmailReceiveSettings(): Promise<UserEmailSettings> {
    const settings = await fetchEmailReceiveSettingsRaw();
    return settings ?? DEFAULT_SETTINGS;
  }

  async updateEmailReceiveSettings(receiveAggregation: string): Promise<UserEmailSettings> {
    await this.ensureUserEmailRowExists();

    const data = await requestGraphql<{ updateEmailReceiveSettings: UserEmailSettings }>(
      updateEmailReceiveSettingsMutation,
      {
        input: {
          receiveAggregation,
        },
      }
    );

    return data.updateEmailReceiveSettings;
  }

  private async ensureUserEmailRowExists(): Promise<void> {
    const existing = await fetchEmailReceiveSettingsRaw();
    if (existing) {
      return;
    }

    const userClient = new UserClient();
    await userClient.fetchUser();

    const syncedSettings = await waitForSyncedUserEmail(
      () => fetchEmailReceiveSettingsRaw(),
      10,
      500
    );
    if (!syncedSettings) {
      throw new Error("user_email not found for userId");
    }
  }
}

const fetchEmailReceiveSettingsQuery = gql`
  query EmailReceiveSettings {
    emailReceiveSettings {
      receiveAggregation
      updatedAt
    }
  }
`;

const updateEmailReceiveSettingsMutation = gql`
  mutation UpdateEmailReceiveSettings($input: UpdateEmailReceiveSettingsInput!) {
    updateEmailReceiveSettings(input: $input) {
      receiveAggregation
      updatedAt
    }
  }
`;
